import { useState } from "react";

import { formatSecondsAsTimer } from "@vericus/cadmus-common";
import {
  ArrowPathIcon,
  CameraIcon,
  CheckIcon,
  CloseIcon,
  ExclamationTriangleIcon,
  VideoCameraIcon,
} from "@vericus/cadmus-icons";
import { Button, Spacer, Text, ToggleGroup } from "@vericus/cadmus-ui";

import {
  type CaptureMode,
  type UseWebcamCaptureReturn,
} from "./use-webcam-capture";
import * as styles from "./webcam-capture-ui.css";

export interface WebcamCaptureUIProps {
  webcam: UseWebcamCaptureReturn;
  isProcessing?: boolean;
  onRetake?: () => void;
  onConfirm?: () => void;
  onCancel?: () => void;
  showModeSelector?: boolean;
  showFooter?: boolean;
  showVideoWarning?: boolean;
}

/**
 * WebcamCaptureUI - Presentation component for webcam capture interface.
 *
 * This component handles the UI/rendering while all logic is managed by the
 * useWebcamCapture hook. Can be used standalone or wrapped in a modal.
 *
 * @example
 * ```tsx
 * const webcam = useWebcamCapture();
 *
 * return (
 *   <WebcamCaptureUI
 *     webcam={webcam}
 *     onConfirm={async () => {
 *       await uploadFile(webcam.capturedMedia.file);
 *     }}
 *   />
 * );
 * ```
 */
export function WebcamCaptureUI({
  webcam,
  isProcessing = false,
  onRetake,
  onConfirm,
  onCancel,
  showModeSelector = true,
  showFooter = true,
  showVideoWarning: showVideoWarningProp,
}: WebcamCaptureUIProps) {
  const [showVideoWarning, setShowVideoWarning] = useState(
    showVideoWarningProp ?? true
  );

  const handleModeChange = (newMode: CaptureMode) => {
    if (webcam.isRecording) {
      webcam.stopRecording();
    }
    if (webcam.capturedMedia != null) {
      webcam.discardCapture();
    }
    webcam.setMode(newMode);

    if (newMode === "video") {
      setShowVideoWarning(true);
    }
  };

  const handleRetake = () => {
    webcam.discardCapture();
    onRetake?.();
  };

  return (
    <div className={styles.container}>
      {/* Mode Selector */}
      {showModeSelector && (
        <div className={styles.modeSelectorContainer}>
          <ToggleGroup.Root
            type="single"
            value={webcam.mode}
            onValueChange={(value) => {
              if (value) handleModeChange(value as CaptureMode);
            }}
            className={styles.modeSelector}
          >
            <ToggleGroup.Item
              value="photo"
              disabled={webcam.isRecording || isProcessing}
              className={styles.modeButton}
            >
              Photo
            </ToggleGroup.Item>
            <ToggleGroup.Item
              value="video"
              disabled={webcam.isRecording || isProcessing}
              className={styles.modeButton}
            >
              Video
            </ToggleGroup.Item>
          </ToggleGroup.Root>
        </div>
      )}

      {/* Video Warning Banner */}
      {showVideoWarning && webcam.mode === "video" && (
        <div className={styles.warningBanner}>
          <ExclamationTriangleIcon
            label="video-length-warning"
            className={styles.warningIcon}
          />
          <Text kind="bodyMd" className={styles.warningText}>
            Video recordings are limited to a maximum of 4 minutes per session.
          </Text>
          <button
            className={styles.warningCloseButton}
            onClick={() => setShowVideoWarning(false)}
            aria-label="Dismiss warning"
          >
            <CloseIcon label="" size={16} />
          </button>
        </div>
      )}

      {/* Camera Preview Area */}
      <div className={styles.videoContainer}>
        {isProcessing && (
          <div className={styles.processingOverlay}>Uploading...</div>
        )}

        {/* Browser Support Error State */}
        {!webcam.browserSupport.supported ? (
          <div className={styles.errorContainer}>
            <Text kind="bodyLg" color="failure">
              {webcam.browserSupport.error}
            </Text>
            <Spacer spacing={12} />
            <Text kind="bodySm">
              Please update your browser or use a different one to access camera
              features.
            </Text>
          </div>
        ) : /* Permission Error State */
        webcam.error != null ? (
          <div className={styles.errorContainer}>
            <Text kind="bodyLg" color="failure">
              {webcam.error.message}
            </Text>
            <Spacer spacing={12} />
            <Button
              kind="primary"
              size="sm"
              onClick={() => webcam.startCamera()}
            >
              Try Again
            </Button>
          </div>
        ) : webcam.capturedMedia != null ? (
          /* Preview Mode */
          <>
            {webcam.capturedMedia.type === "photo" ? (
              <img
                src={webcam.capturedMedia.url}
                alt="Captured photo preview"
                className={styles.video}
              />
            ) : (
              <video
                src={webcam.capturedMedia.url}
                className={styles.video}
                controls
                aria-label="Recorded video preview"
              />
            )}
          </>
        ) : (
          /* Capture Mode */
          <>
            <video
              ref={webcam.videoRef}
              autoPlay
              playsInline
              muted
              className={styles.video}
            />

            {/* Recording Indicator */}
            {webcam.isRecording && (
              <div className={styles.recordingIndicator}>
                <div className={styles.recordingDot} />
                <span className={styles.recordingTime}>
                  {formatSecondsAsTimer(webcam.recordingDuration)} /{" "}
                  {formatSecondsAsTimer(webcam.maxDurationSeconds)}
                </span>
              </div>
            )}

            {/* Capture/Record Button */}
            {webcam.mode === "photo" ? (
              <button
                className={styles.captureButton}
                onClick={webcam.capturePhoto}
                disabled={!webcam.hasPermission || isProcessing}
                aria-label="Take Photo"
              >
                <CameraIcon
                  label="image-capture"
                  className={styles.captureIcon}
                  size={24}
                />
              </button>
            ) : (
              <button
                className={styles.captureButton}
                data-recording={webcam.isRecording}
                onClick={
                  webcam.isRecording
                    ? webcam.stopRecording
                    : webcam.startRecording
                }
                disabled={!webcam.hasPermission || isProcessing}
                aria-label={
                  webcam.isRecording ? "Stop Recording" : "Start Recording"
                }
              >
                {webcam.isRecording ? (
                  <div className={styles.recordingSquare} />
                ) : (
                  <VideoCameraIcon
                    label="video-record"
                    className={styles.captureIcon}
                    size={24}
                  />
                )}
              </button>
            )}
          </>
        )}
      </div>

      {/* Footer Actions */}
      {showFooter && (
        <div className={styles.footer}>
          {webcam.capturedMedia != null ? (
            /* Preview Mode Actions */
            <>
              <Button
                kind="outline"
                size="sm"
                onClick={handleRetake}
                disabled={isProcessing}
                icon={<ArrowPathIcon label="" size={16} />}
              >
                Retake
              </Button>
              <Button
                kind="secondary"
                size="sm"
                onClick={onConfirm}
                disabled={isProcessing}
                icon={<CheckIcon label="" size={16} />}
              >
                {webcam.capturedMedia.type === "video"
                  ? "Use Video"
                  : "Use Photo"}
              </Button>
            </>
          ) : (
            /* Capture Mode Actions */
            <Button
              kind="outline"
              size="sm"
              onClick={onCancel}
              disabled={isProcessing}
            >
              Cancel
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
